import { useState, type FormEvent } from 'react';
import { TextField } from '../../../modules/design-system';
import { createAddress, updateAddress, type ApiClient, type ShippingAddress } from '../../../modules/api';
import { SleekButton } from '../../../modules/theme';
import {
  AddressAutocomplete,
  EMPTY_ADDRESS_FORM,
  addressToForm,
  toAddressParams,
  validateAddressForm,
  type AddressFormErrors,
  type AddressFormValues,
  type ParsedAddress,
} from '../../../pages/Delivery';
import { SLEEK_PAGE_MIN_HEIGHT, SleekPageHeader } from '../shared';

type Props = {
  client: ApiClient;
  /** The player's one address to edit; null adds it. */
  address: ShippingAddress | null;
  onBack: () => void;
  onSaved: (address: ShippingAddress) => void;
};

/** No country field in the mockup; the Places pick fills it, US otherwise. */
const DEFAULT_COUNTRY = 'US';

// State stays optional, as in the shared validation and the backend:
// addresses abroad (and old rows) have none.
const REQUIRED_FIELDS: (keyof AddressFormValues)[] = ['name', 'phone', 'line1', 'city', 'postalCode'];

const isSameForm = (a: AddressFormValues, b: AddressFormValues) =>
  (Object.keys(a) as (keyof AddressFormValues)[]).every((key) => key === 'addressSearch' || a[key].trim() === b[key].trim());

/**
 * Add / edit the player's single shipping address (partner Figma 2026-10-08):
 * one form for both, "Confirm address" lit once every field is filled and,
 * when editing, something actually changed.
 */
export function AddressFormPage({ client, address, onBack, onSaved }: Props) {
  const [initial] = useState<AddressFormValues>(() => (address ? addressToForm(address) : EMPTY_ADDRESS_FORM));
  const [form, setForm] = useState<AddressFormValues>(initial);
  const [errors, setErrors] = useState<AddressFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof AddressFormValues) => (value: string) => setForm((prev) => ({ ...prev, [key]: value }));
  const fieldError = (key: keyof AddressFormValues) => (errors[key] ? { error: errors[key] } : {});

  const handleResolved = (parsed: ParsedAddress) => {
    setForm((prev) => ({ ...prev, ...parsed, addressSearch: parsed.line1 }));
    setErrors({});
  };

  const isFilled = REQUIRED_FIELDS.every((key) => form[key].trim() !== '');
  const canSubmit = isFilled && !submitting && (address === null || !isSameForm(form, initial));

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    const values = { ...form, line1: form.line1 || form.addressSearch, country: form.country || DEFAULT_COUNTRY };
    const found = validateAddressForm(values, true);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const params = toAddressParams(values);
      onSaved(address ? await updateAddress(client, address.id, params) : await createAddress(client, params));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save address');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-page="ship-address" data-co-component="ShipCard" onSubmit={handleSubmit} className={`@container relative flex ${SLEEK_PAGE_MIN_HEIGHT} w-full flex-col`}>
      <SleekPageHeader onBack={onBack} title={address ? 'Edit Address' : 'Add New Address'} />
      <div className="mx-auto flex w-full max-w-[480px] flex-1 flex-col gap-3 px-5">
        <TextField label="Full name" value={form.name} onChange={set('name')} placeholder="First Lastname" autoComplete="name" {...fieldError('name')} />
        <TextField label="Phone" value={form.phone} onChange={set('phone')} placeholder="+1 123 456 7890" inputMode="tel" autoComplete="tel" {...fieldError('phone')} />
        <AddressAutocomplete
          value={form.addressSearch}
          onChange={(text) => setForm((prev) => ({ ...prev, addressSearch: text, line1: text }))}
          onResolved={handleResolved}
          placeholder="Start typing your address..."
        />
        <TextField label="Apartment or unit # (optional)" value={form.line2} onChange={set('line2')} placeholder="Apartment or unit #" />
        <TextField label="City" value={form.city} onChange={set('city')} placeholder="City" {...fieldError('city')} />
        <div className="grid grid-cols-2 gap-3">
          <TextField label="State" value={form.state} onChange={set('state')} placeholder="State" />
          <TextField label="ZIP" value={form.postalCode} onChange={set('postalCode')} placeholder="000000" inputMode="numeric" {...fieldError('postalCode')} />
        </div>
        {error && (
          <p role="alert" className="m-0 text-[12px] text-[var(--co-ShipCard-errorText,#F87171)]">
            {error}
          </p>
        )}
      </div>
      <div className="mx-auto w-full max-w-[480px] px-5 pt-5 pb-6">
        <SleekButton name="ShippingPrimaryButton" type="submit" disabled={!canSubmit} className="w-full">
          {submitting ? 'Saving…' : 'Confirm address'}
        </SleekButton>
      </div>
    </form>
  );
}
